/**
 * The report of a sent schedule that has ended (BA-85 C7–C11): done or ended early, by
 * whom, when, the steps with their done times (or, on a choice board, what was chosen),
 * and the tokens. Send again (Remove, PIN) and Try again (failed) run the normal send flow.
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Action } from '../types/action';
import { Schedule } from '../types/schedule';
import { SentSchedule } from '../types/sentSchedule';
import { chosenStep, formatTime, isChoiceBoard, orderedSteps } from '../utils/sentSchedule';
import { RichText } from './RichText';
import { Fact, NameRow, StepList, StepPicture, Tokens, useStepPictures } from './sentSchedule/parts';

interface SentScheduleReportProps {
  node: SentSchedule;
  deviceId: string;
  actions: Action[];
  schedules?: Schedule[];
  /** C10: who replaced it, when known */
  replacedBy: string | null;
  /** C11: what a failed send replaced, when known */
  failedReplaced: { kind: 'schedule' | 'action'; name: string } | null;
  now: number;
  onDismiss: () => void;
  /** Send again / Try again: opens the send confirm */
  onSendAgain: () => void;
  sending: boolean;
  sendDisabled: boolean;
  /** The send error, already translated */
  sendError?: string;
}

export const SentScheduleReport: React.FC<SentScheduleReportProps> = ({
  node,
  deviceId,
  actions,
  schedules,
  replacedBy,
  failedReplaced,
  now,
  onDismiss,
  onSendAgain,
  sending,
  sendDisabled,
  sendError,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const lang = i18n.language;
  const steps = orderedSteps(node);
  const pictures = useStepPictures(deviceId, steps, actions);
  const failed = node.state === 'failed';
  const choice = isChoiceBoard(node);
  const chosen = chosenStep(node);
  const name = node.schedule?.name ?? '';
  const endedAt = node.endedAt ?? node.sentAt;
  const time = formatTime(endedAt, lang, now);

  const sendButton = (label: string, contained: boolean) => (
    <Box sx={{ mt: 1 }}>
      <Button
        variant={contained ? 'contained' : 'outlined'}
        startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
        onClick={onSendAgain}
        disabled={sending || sendDisabled}
        fullWidth={isPhone}
        sx={isPhone ? { minHeight: 48 } : undefined}
      >
        {sending ? t('schedules.sending') : label}
      </Button>
      {sendError && <Alert severity="error" sx={{ mt: 1.5 }}>{sendError}</Alert>}
    </Box>
  );

  const header = (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
      {failed ? (
        <Typography variant="h6" component="h2" color="error.main"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutlineIcon />
          {t('sentSchedule.failed.title')}
        </Typography>
      ) : (
        <Typography variant="h6" component="h2">{t('sentSchedule.lastSent')}</Typography>
      )}
      <Button onClick={onDismiss} sx={{ flexShrink: 0, ...(isPhone ? { minHeight: 48 } : {}) }}>
        {t('sentSchedule.dismiss')}
      </Button>
    </Box>
  );

  if (failed) {
    return (
      <Card sx={{ borderLeft: 4, borderColor: 'error.main' }}>
        <CardContent>
          {header}
          <NameRow node={node} />
          <Typography variant="body1" sx={{ mb: 1 }}>
            <RichText i18nKey="sentSchedule.failed.body"
              values={{ name, time: formatTime(node.sentAt, lang, now) }} />
            {failedReplaced && (
              <>
                {' '}
                <RichText
                  i18nKey={failedReplaced.kind === 'schedule'
                    ? 'sentSchedule.failed.replacedSchedule' : 'sentSchedule.failed.replacedAction'}
                  values={{ old: failedReplaced.name }}
                />
              </>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('sentSchedule.failed.help')}
          </Typography>
          {sendButton(t('sentSchedule.tryAgain'), true)}
        </CardContent>
      </Card>
    );
  }

  const done = node.state === 'done';
  let line: React.ReactNode;
  if (done) {
    line = t(chosen ? 'sentSchedule.line.chose' : 'sentSchedule.line.done', { time });
  } else if (node.endedBy === 'parent-remove') {
    line = t('sentSchedule.line.removed', { time });
  } else if (node.endedBy === 'tablet-pin') {
    line = t('sentSchedule.line.pin', { time });
  } else if (replacedBy) {
    line = <RichText i18nKey="sentSchedule.line.replaced" values={{ time, name: replacedBy }} />;
  } else {
    line = t('sentSchedule.line.replacedUnknown', { time });
  }
  const offerSendAgain = node.endedBy === 'parent-remove' || node.endedBy === 'tablet-pin';

  const chip = done
    ? <Chip icon={<CheckCircleIcon />} label={t('sentSchedule.chip.done')} color="success" size="small" />
    : <Chip label={t('sentSchedule.chip.endedEarly')} size="small" />;

  const right = choice ? (
    chosen && (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('sentSchedule.chose')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StepPicture step={chosen.step} size={isPhone ? 96 : 120} pictures={pictures} schedules={schedules} />
          <Typography variant="h5" component="p" sx={{ overflowWrap: 'anywhere' }}>{chosen.step.title}</Typography>
        </Box>
      </Box>
    )
  ) : (
    <StepList node={node} steps={steps} live={false} pictures={pictures} schedules={schedules} />
  );

  return (
    <Card>
      <CardContent>
        {header}
        <NameRow node={node} chip={chip} />
        <Box sx={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : '1fr 400px', gap: 3 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>{line}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr 1fr' : 'repeat(3, max-content)',
              columnGap: 5, rowGap: 2, mb: 2 }}>
              <Fact label={t('sentSchedule.tokensEarned')}><Tokens count={node.tokensEarned ?? 0} /></Fact>
              <Fact label={t('sentSchedule.sentAt')}>{formatTime(node.sentAt, lang, now)}</Fact>
              <Fact label={t('sentSchedule.endedAt')}>{time}</Fact>
            </Box>
            {offerSendAgain && (
              <>
                {sendButton(t('sentSchedule.sendAgain'), false)}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('sentSchedule.sendAgainHelp')}
                </Typography>
              </>
            )}
          </Box>
          {right}
        </Box>
      </CardContent>
    </Card>
  );
};
